let attemptPlayAudioUntilSuccess = (audio) => {
    let abort;

    let promise = new Promise((resolve, reject) => {
        let settled = false;
        let animFrame = null;

        abort = (error) => {
            if (settled) throw new Error("already settled");

            if (animFrame) {
                cancelAnimationFrame(animFrame);
            }

            settled = true;
            audio.pause();
            reject(error);
        };

        let attemptPlay = () => {
            animFrame = null;
            let playPromise = audio.play();

            playPromise.then((res) => {
                settled = true;
                resolve(res);
            }, (error) => {
                if (error.name === 'NotAllowedError') {
                    if (settled) return;

                    animFrame = requestAnimationFrame(attemptPlay);
                } else {
                    settled = true;
                    reject(error);
                }
            });
        };

        attemptPlay();
    });

    return [promise, abort];
};

class CooboAudio {
    constructor () {
        this._audio = new Audio();

        this._volume = this._audio.volume;
        this._loop = false;

        this._ctrlPlaying = false;

        this._abort = null;

        this._callingPause = false;
        this._audio.addEventListener('pause', () => {
            if (!this._callingPause) {
                this._setCtrlPlaying(false);
            }
        });

        this._setPlayingListeners = new Set();
    }

    play() {
        if (this._ctrlPlaying) return;

        this._setCtrlPlaying(true);

        return new Promise((resolve, reject) => {
            let [playPromise, abort] = attemptPlayAudioUntilSuccess(this._audio);
            this._abort = abort;

            playPromise.then((res) => {
                this._abort = null;
                resolve(res);
            }, (error) => {
                this._abort = null;
                reject(error);
            });
        });
    }

    pause() {
        if (!this._ctrlPlaying) return;

        this._setCtrlPlaying(false);

        if (this._abort) {
            let error = new DOMException('The play() request was interrupted by a call to pause().', 'AbortError');
            this._abort();
            this._abort = null;
        }

        this._callingPause = true;
        this._audio.pause();
        this._callingPause = false;
    }

    get src() { return this._audio.src; }
    set src(v) {
        if (this._abort) {
            let error = new DOMException('The play() request was interrupted by a new load request.', 'AbortError');
            this._abort(error);
            this._abort = null;
        }

        this._setCtrlPlaying(false);

        this._audio.src = v;
    }
    get duration() { return this._audio.duration; }
    set currentTime(v) { this._audio.currentTime = v; }
    get currentTime() { return this._audio.currentTime; }
    set volume(v) { this._volume = this._audio.volume = v; }
    get volume() { return this._volume; }
    set loop(v) { this._loop = this._audio.loop = v; }
    get loop() { return this._loop; }
    get paused() { return !this._ctrlPlaying; }

    addEventListener(name, ...args) {
        if (name === 'setplaying') {
            this._setPlayingListeners.add(args[0]);
        } else {
            this._audio.addEventListener(name, ...args);
        }
    }

    removeEventListener(name, ...args) {
        if (name === 'setplaying') {
            this._setPlayingListeners.delete(args[0]);
        } else {
            this._audio.removeEventListener(name, ...args);
        }
    }

    _setCtrlPlaying(v) {
        if (v === this._ctrlPlaying) return;

        this._ctrlPlaying = v;

        let e = { value: v };
        for (let f of this._setPlayingListeners) {
            f(e);
        }
    }

    setSrcKeepPlaying(src) {
        let wasPlaying = !this.paused;
        this.src = src;

        if (src && wasPlaying) {
            this.play();
        }
    }

    delete() {
        this._audio.pause();
        this._audio.setAttribute("src", "");
    }
}

export default CooboAudio;
